import { each, reduce, values, keyBy, delay } from 'lodash';
// Legacy constraints for reference:
// Contact constraints
// const constraints = {
//   first_name: { presence: true },
//   last_name: { presence: true },
//   selected_product_id: { presence: true },
//   phone_extension: { numericality: true },
//   email: { presence: true, email: true },
// };
// Opportunity constraints
// const opportunityConstraints = {
//   name: { presence: true },
//   amount: { numericality: { notLessThan: 0 } },
// };
// Account constraints (used within opportunity validation)
// const accountConstraints = {
//   name: { presence: true },
//   annual_revenue: { numericality: { notLessThan: 0 } },
//   number_of_employees: { numericality: { notLessThan: 0 } },
//   website: { presence: { allowEmpty: true }, url: true },
// };
import Joi from 'joi';
import callApi from '../../util/apiCaller';

export const WINLOSS_LOAD_OPPORTUNITIES = 'WINLOSS_LOAD_OPPORTUNITIES';
export const WINLOSS_UPDATE_OPPORTUNITY = 'WINLOSS_UPDATE_OPPORTUNITY';
export const WINLOSS_RESET = 'WINLOSS_RESET';
export const WINLOSS_RESET_SELECTED_OPPORTUNITIES = 'WINLOSS_RESET_SELECTED_OPPORTUNITIES';
export const WINLOSS_TOGGLE_ALL = 'WINLOSS_TOGGLE_ALL';
export const WINLOSS_SELECT_OPPORTUNITY = 'WINLOSS_SELECT_OPPORTUNITY';
export const WINLOSS_UNSELECT_OPPORTUNITY = 'WINLOSS_UNSELECT_OPPORTUNITY';
export const WINLOSS_EDIT_OPPORTUNITY = 'WINLOSS_EDIT_OPPORTUNITY';
export const WINLOSS_EDIT_OPPORTUNITY_SELECT_CONTACTS = 'WINLOSS_EDIT_OPPORTUNITY_SELECT_CONTACTS';
export const WINLOSS_EDIT_OPPORTUNITY_EDIT_CONTACT = 'WINLOSS_EDIT_OPPORTUNITY_EDIT_CONTACT';
export const WINLOSS_EDIT_OPPORTUNITY_UPDATE_CONTACT = 'WINLOSS_EDIT_OPPORTUNITY_UPDATE_CONTACT';
export const WINLOSS_LOAD_TRACKINGS = 'WINLOSS_LOAD_TRACKINGS';
export const WINLOSS_SUBMIT_SAMPLES = 'WINLOSS_SUBMIT_SAMPLES';
export const WINLOSS_LOAD_CONTACT_DISPOSITIONS = 'WINLOSS_LOAD_CONTACT_DISPOSITIONS';

export const WINLOSS_FETCH_SAVED_PARAMS = 'WINLOSS_FETCH_SAVED_PARAMS';
export const WINLOSS_ADD_SAVED_PARAMS = 'WINLOSS_ADD_SAVED_PARAMS';
export const WINLOSS_REMOVE_SAVED_PARAMS = 'WINLOSS_REMOVE_SAVED_PARAMS';
export const WINLOSS_FETCH_UNLINKED_CONTACTS = 'WINLOSS_FETCH_UNLINKED_CONTACTS';

export const WINLOSS_FETCH_SUBMITTED_PARAMS = 'WINLOSS_FETCH_SUBMITTED_PARAMS';

export const WINLOSS_MASS_FETCH_OPPORTUNITY_CONTACTS = 'WINLOSS_MASS_FETCH_OPPORTUNITY_CONTACTS';

export function validateOpportunityContact(contact) {
  const schema = Joi.object({
    first_name: Joi.string().required().messages({
      'string.base': 'First name must be a string',
      'string.empty': 'First name is required',
      'any.required': 'First name is required',
    }),
    last_name: Joi.string().required().messages({
      'string.base': 'Last name must be a string',
      'string.empty': 'Last name is required',
      'any.required': 'Last name is required',
    }),
    selected_product_id: Joi.any().required().messages({
      'any.required': 'Product selection is required',
    }),
    phone_extension: Joi.number().allow('', null).messages({
      'number.base': 'Phone extension must be a number',
    }),
    email: Joi.string().email({ tlds: { allow: false } }).required().messages({
      'string.base': 'Email must be a string',
      'string.empty': 'Email is required',
      'any.required': 'Email is required',
      'string.email': 'Email must be a valid email address',
    }),
  }).unknown(true);
  const { error } = schema.validate(contact, { abortEarly: false });
  if (!error) return {};
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
}
export function validateOpportunity(opportunity) {
  const schema = Joi.object({
    name: Joi.string().required().messages({
      'string.base': 'Name must be a string',
      'string.empty': 'Name is required',
      'any.required': 'Name is required',
    }),
    amount: Joi.number().min(0).messages({
      'number.base': 'Amount must be a number',
      'number.min': 'Amount must be at least 0',
    }),
    account: Joi.object({
      name: Joi.string().required().messages({
        'string.base': 'Account name must be a string',
        'string.empty': 'Account name is required',
        'any.required': 'Account name is required',
      }),
      annual_revenue: Joi.number().min(0).allow('', null).messages({
        'number.base': 'Annual revenue must be a number',
        'number.min': 'Annual revenue must be at least 0',
      }),
      number_of_employees: Joi.number().min(0).allow('', null).messages({
        'number.base': 'Number of employees must be a number',
        'number.min': 'Number of employees must be at least 0',
      }),
      website: Joi.string().uri().allow('', null).messages({
        'string.base': 'Website must be a string',
        'string.uri': 'Website must be a valid URL',
      }),
    }).allow(null).unknown(true),
    selected_contacts: Joi.array().min(1).required().messages({
      'array.base': 'At least one contact must be selected',
      'array.min': 'At least one contact must be selected',
      'any.required': 'At least one contact must be selected',
    }),
  }).unknown(true);
  const { error } = schema.validate(opportunity, { abortEarly: false });
  if (!error) return {};
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
}

export function resetOpportunities() {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_LOAD_OPPORTUNITIES, opportunities: [], status: null, errors: {},
    });
    return Promise.resolve();
  };
}

export function fetchOpportunities(criteria) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_LOAD_OPPORTUNITIES, opportunities: [], indexedContacts: {}, status: 'loading', errors: null,
    });

    const included = [
      'owners',
      'accounts',
      'accountNames', // this will override the account attributes, only return account names
      'lineItems',
      'contactRoles',
      // 'contacts',
      'opportunityCompetitors',
      'logHistories',
    ];
    const { team } = criteria;
    const teamId = team && team._id ? `&teamId=${team._id}` : '';
    const p = callApi(`search/nominations?include=${included.join(',')}${teamId}`, 'POST', criteria);
    p.then(
      (res) => {
        const {
          owners, opportunities, accounts, contacts,
        } = res;
        each(contacts, (contact) => {
          if (accounts[contact.account_id]) {
            if (!accounts[contact.account_id].contacts) {
              accounts[contact.account_id].contacts = [];
            }
            if (!accounts[contact.account_id].indexedContacts) {
              accounts[contact.account_id].indexedContacts = {};
            }
            accounts[contact.account_id].indexedContacts[contact.contact_id] = contact;
            accounts[contact.account_id].contacts.push({
              ...contact,
              isOpportunityContact: false,
            });
          }
        });
        opportunities.map((opportunity) => {
          opportunity.submitted = false;
          opportunity.owner = owners[opportunity.owner_id] || null;
          opportunity.account = accounts[opportunity.account_id] || null;
          opportunity.outcome = opportunity.stage;
          const uniqRoles = values(keyBy(opportunity.contact_roles, 'contact_id')); // one contact can be assined multiple roles

          opportunity.contacts = reduce(uniqRoles, (results, contactRole) => {
            if (contacts[contactRole.contact_id]) {
              results.push({ ...contacts[contactRole.contact_id], ...contactRole });
            }
            return results;
          }, []);
          return opportunity;
        });
        return dispatch({
          type: WINLOSS_LOAD_OPPORTUNITIES, opportunities, indexedContacts: contacts, status: 'loaded', errors: null,
        });
      },
      errors => dispatch({
        type: WINLOSS_LOAD_OPPORTUNITIES, opportunities: [], indexedContacts: {}, status: 'error', errors,
      }),
    );
    return p;
  };
}

/*
reducer:
winLoss = {
  id: {
    ...opportunity
  }
}
}
*/

export function winLossSelectOpportunity(opportunity, tvProductMappings) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_SELECT_OPPORTUNITY,
      opportunity,
      tvProductMappings,
    });
    return Promise.resolve();
  };
}
export function winLossUnselectOpportunity(opportunity) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_UNSELECT_OPPORTUNITY,
      opportunity,
    });
    return Promise.resolve();
  };
}
export function winLossEditOpportunity(opportunity) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_EDIT_OPPORTUNITY,
      opportunity,
    });
    return Promise.resolve();
  };
}

export function winLossUpdateOpportunity(opportunityId, params) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_UPDATE_OPPORTUNITY,
      opportunityId,
      params,
    });
    return Promise.resolve(params);
  };
}

export function winLossEditOpportunitySelectContacts(contacts) {
  return dispatch => dispatch({
    type: WINLOSS_EDIT_OPPORTUNITY_SELECT_CONTACTS,
    contacts,
  });
}

export function winLossEditOpportunityEditContact(contact) {
  return dispatch => dispatch({
    type: WINLOSS_EDIT_OPPORTUNITY_EDIT_CONTACT,
    contact,
  });
}

export function winLossEditOpportunityUpdateContact(contact) {
  return dispatch => dispatch({
    type: WINLOSS_EDIT_OPPORTUNITY_UPDATE_CONTACT,
    contact,
  });
}

export function winLossLoadContactDispositions(clientId, opportunityId, contactId) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_LOAD_CONTACT_DISPOSITIONS, opportunityId, contactId, activities: [], status: 'loading', errors: null,
    });
    return callApi(`truvoice/clients/${clientId}/opportunities/${opportunityId}/contacts/${contactId}/activities`, 'GET').then(
      results => dispatch({
        type: WINLOSS_LOAD_CONTACT_DISPOSITIONS, activities: results.activities, opportunityId, contactId, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: WINLOSS_LOAD_CONTACT_DISPOSITIONS, activities: [], opportunityId, contactId, status: 'error', errors: error.data || error,
      }),
    );
  };
}

export function winLossReset() {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_RESET,
    });
    return Promise.resolve();
  };
}

export function winLossResetSelectedOpportunities() {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_RESET_SELECTED_OPPORTUNITIES,
    });
    return Promise.resolve();
  };
}

export function winLossToogleAll(savedOnly = false, tvProductMappings = {}) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_TOGGLE_ALL,
      savedOnly,
      tvProductMappings,
    });
    return Promise.resolve();
  };
}

export function fetchClientTrackings(clientId, initiativeId, opportunityIds = [], loadSavedParams = false) {
  return (dispatch) => {
    if (opportunityIds.length === 0) {
      dispatch({
        type: WINLOSS_LOAD_TRACKINGS, trackings: {}, status: 'loaded', errors: null,
      });
      if (loadSavedParams) {
        dispatch({
          type: WINLOSS_FETCH_SAVED_PARAMS, opportunities: [], status: 'loaded', errors: null,
        });
      }
      dispatch({
        type: WINLOSS_FETCH_SUBMITTED_PARAMS, opportunities: [], status: 'loaded', errors: null,
      });
      return Promise.resolve();
    }

    const promises = [];
    dispatch({
      type: WINLOSS_LOAD_TRACKINGS, trackings: {}, status: 'loading', errors: null,
    });
    const p1 = callApi(`truvoice/clients/${clientId}/initiatives/${initiativeId}/trackings`, 'POST', { opportunity_ids: opportunityIds });
    p1.then(
      () => {},
      error => dispatch({
        type: WINLOSS_LOAD_TRACKINGS, trackings: {}, status: 'error', errors: error,
      }),
    );
    promises.push(p1);

    if (loadSavedParams) {
      dispatch({
        type: WINLOSS_FETCH_SAVED_PARAMS, opportunities: [], status: 'loading', errors: null,
      });
      const p2 = callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/opportunities`, 'POST', { ids: opportunityIds });
      p2.then(
        () => {},
        error => dispatch({
          type: WINLOSS_FETCH_SAVED_PARAMS, opportunities: [], status: 'error', errors: error,
        }),
      );
      promises.push(p2);
    } else {
      promises.push(Promise.resolve({ opportunities: [] }));
    }

    dispatch({
      type: WINLOSS_FETCH_SUBMITTED_PARAMS, opportunities: [], status: 'loading', errors: null,
    });
    const p3 = callApi(`submitted-nominations/clients/${clientId}/initiatives/${initiativeId}/opportunities`, 'POST', { ids: opportunityIds });
    p3.then(
      () => {},
      error => dispatch({
        type: WINLOSS_FETCH_SUBMITTED_PARAMS, opportunities: [], status: 'error', errors: error,
      }),
    );

    promises.push(p3);
    const allP = Promise.all(promises);
    allP.then(([trackings, result2, result3]) => {
      // because of race condition
      dispatch({
        type: WINLOSS_LOAD_TRACKINGS, trackings, status: 'loaded', errors: null,
      });
      dispatch({
        type: WINLOSS_FETCH_SAVED_PARAMS, opportunities: result2.opportunities, status: 'loaded', errors: null,
      });
      dispatch({
        type: WINLOSS_FETCH_SUBMITTED_PARAMS, opportunities: result3.opportunities, status: 'loaded', errors: null,
      });
    });
    return allP;
  };
}
export function resetClientTrackings() {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_LOAD_TRACKINGS, trackings: {}, status: null, errors: null,
    });
    return Promise.resolve();
  };
}


export function submitSample(clientId, programId, params) {
  return (dispatch) => {
    const p = callApi(`truvoice/clients/${clientId}/initiatives/${programId}/samples`, 'POST', params);
    p.then((results) => {
      const addedSample = results.added_sample;
      dispatch({ type: WINLOSS_SUBMIT_SAMPLES, addedSample });
    });
    return p;
  };
}

export function addSavedParams(clientId, initiativeId, opportunities = []) {
  return (dispatch) => {
    return callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/opportunities/add`, 'POST', { opportunities }).then(
      (results) => {
        return dispatch({
          type: WINLOSS_ADD_SAVED_PARAMS, opportunities: results.opportunities, error: null,
        });
      },
      error => dispatch({
        type: WINLOSS_ADD_SAVED_PARAMS, opportunities: [], error,
      }),
    );
  };
}

export function removeSavedParams(clientId, initiativeId, ids = []) {
  return (dispatch) => {
    return callApi(`saved-nominations/clients/${clientId}/initiatives/${initiativeId}/opportunities/remove`, 'POST', { ids }).then(
      () => {
        return dispatch({
          type: WINLOSS_REMOVE_SAVED_PARAMS, ids, error: null,
        });
      },
      error => dispatch({
        type: WINLOSS_REMOVE_SAVED_PARAMS, ids: [], error,
      }),
    );
  };
}

export function resetSavedParams() {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_FETCH_SAVED_PARAMS, opportunities: [], status: null, errors: null,
    });
    return Promise.resolve();
  };
}


export function fetchUnlinkedContacts(clientId, initiativeId) {
  return (dispatch) => {
    const promises = [];
    dispatch({
      type: WINLOSS_FETCH_UNLINKED_CONTACTS, contacts: [], status: 'loading', errors: null,
    });
    const p1 = callApi(`truvoice/clients/${clientId}/initiatives/${initiativeId}/unlinked-contacts`, 'GET');
    p1.then(
      ({ contacts }) => dispatch({
        type: WINLOSS_FETCH_UNLINKED_CONTACTS, contacts, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: WINLOSS_FETCH_UNLINKED_CONTACTS, contacts: [], status: 'error', errors: error,
      }),
    );
    promises.push(p1);
    return Promise.all(promises);
  };
}

export function winLossMassFetchOpportunityContacts(opportunityIds) {
  return (dispatch) => {
    dispatch({
      type: WINLOSS_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts: [], accounts: [], status: 'loading', errors: null,
    });
    return callApi('search/nominations/opportunities/details', 'POST', { ids: opportunityIds }).then(
      ({ contacts, accounts }) => dispatch({
        type: WINLOSS_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts, accounts, status: 'loaded', errors: null,
      }),
      error => dispatch({
        type: WINLOSS_MASS_FETCH_OPPORTUNITY_CONTACTS, opportunityIds, contacts: [], accounts: [], status: 'error', errors: error.data || error,
      }),
    );
  };
}

export function winLossFetchOpportunityContacts(opportunityId) {
  return winLossMassFetchOpportunityContacts([opportunityId]);
}
